// A page that is a file. The doc is the truth and the file is printed from
// it; whatever else writes the file (git, another editor) is taken back in
// as edits. The host owns the file: it reads it with a version, and writes
// only over the version the page last saw, so a save never overwrites a
// change it hasn't taken in.
//
// With a room (the machine's, for everyone with the file open) the page is
// edited together: the first in the room opens it as above and is its
// writer, the only one that saves and takes the disk back in; anyone after
// starts empty and takes the page from the room, so two copies made from
// the file apart are never merged into a doubled page. When the writer
// leaves, the earliest still there takes over.
// meets: file/no-overwrite, files/open-is-live
import { Editor, type Options } from "./editor";
import { parseMarkdown } from "./markdown";
import type { Transport } from "./sync";

export type Room = {
  transport: Transport; // to and from everyone else with the file open
  writer: boolean; // the first in the room
  others: number; // how many were there before this one
  onWriter(f: (writer: boolean) => void): void; // this one became, or stopped being, the writer
};

// How long a page taken from the room may take to arrive before it is made
// from the file instead (those in the room had gone, unnoticed yet).
const ADOPT_WAIT = 15_000;

export type FileHost = {
  path: string; // names the page kept in this browser, so unique per file
  title?: string; // what the bar shows, if not the path
  read(): Promise<{ text: string; version: string }>;
  // Writes `text` if the file is still at version `base`; otherwise says it
  // changed, and writes nothing.
  write(text: string, base: string): Promise<{ version: string } | { changed: true }>;
};

export type FileState = "saved" | "unsaved" | "saving" | "failed";
type Frontiers = ReturnType<Editor["page"]["doc"]["frontiers"]>;

export class FileSession {
  state: FileState = "saved";
  why?: string;
  private saved?: string; // the text this tab last read or wrote
  private timer?: ReturnType<typeof setTimeout>;
  private saving?: Promise<boolean>;
  private listeners = new Set<() => void>();
  private stop: () => void;

  private constructor(readonly editor: Editor, readonly host: FileHost, readonly autosave?: number, public writer = true) {
    this.stop = editor.page.doc.subscribe((batch) => {
      if (String(batch.origin ?? "").startsWith("aside:file")) return;
      if (batch.events.every((e) => e.target === editor.page.file.id)) {
        // The writer marked what it saved: so is what this one shows.
        if (!this.writer && batch.by === "import") this.set("saved");
        return;
      }
      this.set("unsaved");
      // Anyone's edit, this one's or the room's, is saved by the writer.
      if (this.autosave === undefined || !this.writer) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => void this.save(), this.autosave);
    });
  }

  // Open the host's file: the page kept in this browser if there is one,
  // with whatever changed on disk since taken in; else made from the file.
  // In a room others are in already, the page is theirs.
  static async open(host: FileHost, opts: Options & { autosave?: number; room?: Room } = {}): Promise<FileSession> {
    const room = opts.room;
    if (room && room.others > 0 && !room.writer) {
      const editor = await Editor.open(`file:${host.path}`, { ...opts, also: room.transport, adopt: true });
      const s = new FileSession(editor, host, opts.autosave, false);
      let promoted = false;
      await new Promise<void>((ok) => {
        const stop = editor.page.doc.subscribe(() => !editor.page.fresh && (stop(), ok()));
        room.onWriter((w) => w && ((promoted = true), ok()));
        setTimeout(ok, ADOPT_WAIT);
      });
      if (editor.page.fresh) {
        // Nobody answered: the page is made from the file, as the first's is.
        const disk = await host.read();
        if (opts.plain) editor.page.seedText(disk.text);
        else {
          const read = parseMarkdown(disk.text);
          editor.page.seed(read.nodes, {}, { tail: read.tail });
        }
        s.take(disk);
      }
      if (promoted) await s.promote();
      room.onWriter((w) => void (w ? s.promote() : s.demote()));
      s.set(s.writer ? s.state : "saved");
      return s;
    }
    const disk = await host.read();
    const editor = await Editor.open(`file:${host.path}`, { ...opts, file: disk.text, also: room?.transport });
    const s = new FileSession(editor, host, opts.autosave);
    room?.onWriter((w) => void (w ? s.promote() : s.demote()));
    s.take(disk);
    s.set(editor.source().text === disk.text ? "saved" : "unsaved");
    return s;
  }

  // Dropped from the room and back after someone else took over: they save.
  demote() {
    this.writer = false;
    clearTimeout(this.timer);
  }

  // The writer left the room and this one is next: from now on it saves,
  // starting with whatever the room edited since the last save.
  async promote(): Promise<void> {
    if (this.writer) return;
    this.writer = true;
    await this.reload();
    if (this.state === "unsaved" && this.autosave !== undefined) await this.save();
  }

  get version(): string | undefined {
    const v = this.editor.page.file.get("version");
    return typeof v === "string" ? v : undefined;
  }

  private get at(): Frontiers | undefined {
    const at = this.editor.page.file.get("at");
    return typeof at === "string" ? JSON.parse(at) : undefined;
  }

  // The page was the file's `version` at `at`.
  private mark(version: string, at: Frontiers) {
    const f = this.editor.page.file;
    f.set("version", version);
    f.set("at", JSON.stringify(at));
    this.editor.page.doc.commit({ origin: "aside:file" });
  }

  // Take in the file as it is on disk, if it changed since the page last
  // saw it.
  take(disk: { text: string; version: string }) {
    this.saved = disk.text;
    if (disk.version === this.version) return;
    const at = this.at ?? this.editor.page.doc.frontiers();
    this.mark(disk.version, this.editor.absorb(disk.text, at));
  }

  async reload(): Promise<void> {
    if (!this.writer) return; // the writer takes the disk in, for the room
    const disk = await this.host.read();
    this.take(disk);
    this.set(this.editor.source().text === this.saved ? "saved" : "unsaved");
  }

  // Print the page and write it over the version last seen; if the file
  // changed meanwhile, take that in, merged, and write again.
  save(): Promise<boolean> {
    clearTimeout(this.timer);
    if (!this.writer) return Promise.resolve(true); // the room's writer saves for everyone
    return (this.saving ??= this.write().finally(() => (this.saving = undefined)));
  }

  private async write(): Promise<boolean> {
    this.set("saving");
    try {
      for (let tries = 0; tries < 3; tries++) {
        const at = this.editor.page.doc.frontiers();
        const text = this.editor.source().text;
        if (text === this.saved && this.version !== undefined) return this.set("saved"), true;
        const r = await this.host.write(text, this.version ?? "");
        if ("version" in r) {
          // What was written, read back in: the blocks now keep the lines
          // they were printed as, so the next edit is diffed against them.
          this.saved = text;
          this.mark(r.version, this.editor.absorb(text, at));
          this.set(this.editor.source().text === text ? "saved" : "unsaved");
          return true;
        }
        this.take(await this.host.read());
      }
      this.why = "The file kept changing while saving; nothing was overwritten.";
      this.set("failed");
      return false;
    } catch (e) {
      this.why = `The file wasn't saved: ${(e as Error).message ?? e}`;
      this.set("failed");
      return false;
    }
  }

  subscribe(f: () => void): () => void {
    this.listeners.add(f);
    return () => this.listeners.delete(f);
  }

  private set(state: FileState) {
    if (state !== "failed") this.why = undefined;
    this.state = state;
    this.listeners.forEach((f) => f());
  }

  close() {
    clearTimeout(this.timer);
    this.stop();
    this.editor.close();
  }
}
